import logger from '../../utils/logger';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../../hooks/useAuth';
import { ArrowRight, Eye, EyeOff, Loader } from 'lucide-react';
import axios from 'axios';
import AuthCard from '../brand/AuthCard';

const inputClass =
  'w-full px-3 py-2.5 bg-[rgba(20,18,28,0.7)] border border-white/10 rounded-lg text-sm text-white placeholder-white/25 outline-none focus:border-violet-400/55 focus:bg-[rgba(24,21,34,0.9)] focus:ring-2 focus:ring-violet-500/20 transition-colors';

const LoginForm = ({ onToggleForm, onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!email || !password) {
      setError('Please enter both email and password');
      setIsLoading(false);
      return;
    }

    try {
      const result = await login(email, password);
      if (!result.success) {
        setError(result.error);
      } else if (result.mfaRequired) {
        if (onLoginSuccess) {
          onLoginSuccess({ ...result, email, password });
        }
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      logger.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setIsLoading(true);

    try {
      const response = await axios.post('/api/auth/google/callback', {
        credential: credentialResponse.credential
      });

      if (response.data.success) {
        const { token, user } = response.data.data;
        if (token) localStorage.setItem('token', token);
        if (user) localStorage.setItem('user', JSON.stringify(user));
        window.location.href = '/home';
      } else {
        setError(response.data.message || 'Google Sign-In failed');
      }
    } catch (err) {
      logger.error('Google Sign-In error:', err);
      setError(err.response?.data?.message || 'Google Sign-In failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google Sign-In was cancelled or failed. Please try again.');
  };

  const footerNote = (
    <>
      By signing in, you agree to our{' '}
      <a href="/legal?doc=terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-violet-300 transition-colors">Terms</a>
      {', '}
      <a href="/legal?doc=privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-violet-300 transition-colors">Privacy</a>
      {' & '}
      <a href="/legal?doc=refund" target="_blank" rel="noopener noreferrer" className="underline hover:text-violet-300 transition-colors">Refund Policy</a>
    </>
  );

  return (
    <AuthCard
      title="Welcome back"
      subtitle="Sign in to your Studio account"
      footerNote={footerNote}
    >
      {error && (
        <div className="mb-4 px-3 py-2.5 rounded-md border border-red-500/30 bg-red-500/10">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-xs font-medium text-white/60 mb-1.5">Email</label>
          <input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@company.com"
            className={inputClass}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="password" className="block text-xs font-medium text-white/60">Password</label>
            <Link to="/forgot-password" className="text-xs text-white/50 hover:text-violet-300 transition-colors">Forgot?</Link>
          </div>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className={`${inputClass} pr-10`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-white/40 hover:text-white/70 transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white text-sm font-semibold rounded-md transition-all inline-flex items-center justify-center gap-2 shadow-lg shadow-violet-900/20 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <><Loader size={16} className="animate-spin" /> Signing in...</>
          ) : (
            <>Sign in <ArrowRight size={16} /></>
          )}
        </button>
      </form>

      <div className="my-5 flex items-center gap-3">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-xs text-white/30">or</span>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      <div className="flex justify-center">
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={handleGoogleError}
          useOneTap={false}
          theme="filled_black"
          size="large"
          text="signin_with"
          shape="rectangular"
          logo_alignment="center"
        />
      </div>

      <p className="mt-6 text-center text-sm text-white/55">
        Don't have an account?{' '}
        <button onClick={onToggleForm} className="font-semibold text-violet-300 hover:text-violet-200 transition-colors">
          {process.env.REACT_APP_INVITE_ONLY !== 'false' ? 'Request an invitation' : 'Start free trial'}
        </button>
      </p>
    </AuthCard>
  );
};

export default LoginForm;
